import React, { useEffect, useRef, useState } from "react";

function useDebouncedValue(val, ms = 300) {
  const [v, setV] = useState(val);

  useEffect(() => {
    const t = setTimeout(() => setV(val), ms);
    return () => clearTimeout(t);
  }, [val, ms]);

  return v;
}

export default function GenericSelect2AutocompleteField({
  field,
  value,
  letterId,
  fetcher,
  onChange,
  onPickEntity,
  placeholder = "Cerca...",
  minChars = 0,
  preloadOnFocus = false,
  forward = {},
}) {
  const initialText =
    typeof value === "string"
      ? value
      : value?.text || value?.value || "";

  const [inputValue, setInputValue] = useState(initialText);
  const [query, setQuery] = useState(initialText);
  const debounced = useDebouncedValue(query, 350);

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const boxRef = useRef(null);
  const abortRef = useRef(null);
  const hasLoadedInitialRef = useRef(false);

  useEffect(() => {
    const handler = (ev) => {
      if (boxRef.current && !boxRef.current.contains(ev.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const nextText =
      typeof value === "string"
        ? value
        : value?.text || value?.value || "";

    setInputValue(nextText);
    setQuery(nextText);
  }, [value]);

  const runSearch = async (term, pageArg = 1, append = false) => {
    if (typeof fetcher !== "function") return;
    if (!field?.placeholder) return;

    const q = (term || "").trim();

    if (q.length < minChars) {
      if (!(preloadOnFocus && q.length === 0)) {
        setItems([]);
        setHasMore(false);
        setPage(1);
        return;
      }
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const data = await fetcher(
        {
          q,
          lettera_id: Number(letterId),
          field: field.placeholder,
          forward,
          page: pageArg,
          page_size: 25,
        },
        controller.signal
      );

      const list = Array.isArray(data?.results) ? data.results : [];
      const more = !!data?.pagination?.more;

      setHasMore(more);
      setPage(pageArg);
      setItems((prev) => (append ? [...prev, ...list] : list));
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Generic autocomplete error:", error);
        setItems([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    const q = (debounced || "").trim();

    if (q.length < minChars) {
      if (preloadOnFocus && q.length === 0) {
        runSearch("", 1, false);
      } else {
        setItems([]);
        setHasMore(false);
        setPage(1);
      }
      return;
    }

    runSearch(q, 1, false);
  }, [debounced, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePick = (item) => {
    const text = item?.text || item?.value || "";

    setInputValue(text);
    setQuery(text);
    setOpen(false);

    onChange?.({
      id: item?.id ?? null,
      text,
      value: text,
    });
    onPickEntity?.(field?.anagrafica_model, item);
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (!hasMore || loading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
      runSearch(query, page + 1, true);
    }
  };

  const handleFocus = () => {
    setOpen(true);

    if (!preloadOnFocus) return;
    if (hasLoadedInitialRef.current) return;

    hasLoadedInitialRef.current = true;
    runSearch(inputValue || "", 1, false);
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
        value={inputValue}
        onChange={(e) => {
          const next = e.target.value;
          setInputValue(next);
          setQuery(next);
          setOpen(true);
          onChange?.(next);
        }}
        onFocus={handleFocus}
        placeholder={placeholder}
      />

      {open && (
        <div
          className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-60 overflow-auto"
          onScroll={handleScroll}
        >
          {loading && !items.length ? (
            <div className="px-3 py-2 text-sm text-gray-500">Caricamento…</div>
          ) : items.length ? (
            <>
              {items.map((item, idx) => (
                <button
                  key={`${item.id ?? item.text}-${idx}`}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50"
                  onClick={() => handlePick(item)}
                >
                  {item.text}
                </button>
              ))}

              {loading && (
                <div className="px-3 py-2 text-xs text-gray-400">
                  Caricamento altri risultati…
                </div>
              )}
            </>
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">
              Nessun risultato
            </div>
          )}
        </div>
      )}
    </div>
  );
}